import { ActionWithOptionalPayload } from 'common/store/typings';
import { CommonContextActionTypes } from 'common/store/context/actionTypes';
import { CommonContextState } from 'common/store/context/types';
import { UserObject } from 'common/store/session/reducer';
import { HitData } from 'common/store/common/search/types';
import {
    ContextTypeKeys, PartyContextValues, QuestionContextValues,
} from './types';
import { RecordingContextValues } from '../../features/recordingHeader/types';
import { SpeakingQuestionSetIds } from '../../services/speakingQuestionSetService';

export type WorkflowInfo = {
    evaluatorState?: string;
    evaluatorPrimaryAction?: string;
    evaluatorSecondaryActions?: [];
    plannerState?: string;
    plannerPrimaryAction?: string;
    plannerSecondaryActions?: [];
    moderationStatus?: string;
    moderatorPrimaryAction?: string;
    isModerator?: boolean;
    moderationComment?: string;
    numberOfCandidatesForContext?: number;
    numberOfGradedCandidatesForContext?: number;
    numberOfCandidatesWithConflict?: number;
    numberOfNotReadyForGradingIssues?: number;
    numberOfRemainingCandidatesToGrade?: number;
    firstNotReadyForGradingIssue?: string;
    numberOfEvaluatorsWaitingFor?: number;
    finalConfirmationDate?: string;
    numberOfSubmissionsForContext?: number;
    evaluatorsWaitingFor?: UserObject[];
    openScreenIdVerification?: boolean;
    numberOfNotSubmittedCandidates?: number;
    longestDeliveryDeadlineWithExtraTime?: string;
};

export enum AssessmentRunSessionStatus {
    CREATED = 'created',
    IN_PROGRESS = 'in_progress',
    FINISHED = 'finished',
    CLOSED = 'closed',
}

export enum AssessmentRunSessionContributorRole {
    EVALUATOR = 'evaluator',
    CANDIDATE = 'candidate',
}

export enum ContextDisplayStatus {
    // Context display has been requested by the examiner
    CONTEXT_DISPLAY_REQ = 'context_display_req',

     // Context display has been acknowledged by the candidate.
    CONTEXT_DISPLAY_ACK = 'context_display_ack',

    // Context hide has been requested by the examiner.
    CONTEXT_HIDE_REQ = 'context_hide_req',

    // Context hide has been acknowledged by the candidate.
    CONTEXT_HIDE_ACK = 'context_hide_ack',

}
export type AssessmentRunSessionContributor = {
    assessmentRunSessionContributorId: number;
    role: AssessmentRunSessionContributorRole;
    userId: number;
    status: AssessmentRunSessionStatus;
    contextDisplayStatus?: ContextDisplayStatus;
    extraInfo: AssessmentRunSessionContributorExtraInfo;
    candidateExternalId?: string;
}

export type AssessmentRunSessionContributorExtraInfo = {
    accessArrangement: boolean;
    accessArrangementsDetails: string;
    identityNumber: string;
    minor: boolean;
    photos: [{
        type: string;
        url: string;
    }];
    firstName: string;
    lastName: string;
}

export type AssessmentRunSession = {
    assessmentRunSessionId?: number;
    status?: AssessmentRunSessionStatus;
    contextObjectId?: number;
    contextObjectStartTime?: number;
    contributors?: AssessmentRunSessionContributor[];
    workflowKey?: string;
    resource?: {[index: string]: any};
    contextDisplayStatus?: ContextDisplayStatus;
    startTime?: number;
    serverLocalTime?: string;
}

export interface ContextState extends CommonContextState {
    readonly assessmentRunId: number;
    readonly partyId: number;
    readonly partiesList?: HitData[];
    readonly questionId: number;
    readonly selectedCommitteeId?: number;
    readonly partyState: PartyContextValues;
    readonly questionState: QuestionContextValues;
    readonly workflowInfo?: WorkflowInfo;
    readonly recordingContextValues?: RecordingContextValues;
    readonly assessmentRunSession?: AssessmentRunSession;
    readonly speakingQuestionSetIds?: SpeakingQuestionSetIds;
    readonly speakingSelectedContentItemId?: number;
    readonly showDetailedView?: boolean;
    readonly candidateSubmissionAccessLevel?: string;
}

export const initialState: ContextState = {
    assessmentRunId: -1,
    partyId: -1,
    questionId: -1,
    workspaceId: '',
    selectedCommitteeId: -1,
    partyState: {
        partyName: '',
        partyCommitteeId: -1,
    },
    questionState: {
        documentContentRevisionId: -1,
        questionType: '',
        questionNumber: -1,
        questionNumbering: '',
        questionName: '',
        answered: false,
    },
    workflowInfo: {},
    showDetailsPanel: false,
    recordingContextValues: null,
    candidateSubmissionAccessLevel: undefined,
};

export const contextReducer = (state: ContextState = initialState, action: ActionWithOptionalPayload): ContextState => {
    switch (action.type) {
    case ContextTypeKeys.SET_PARTY_STATE:
        return {
            ...state,
            partyState: {
                ...state.partyState,
                ...action.payload,
            },
        };
    case 'SET_CANDIDATE_SUBMISSION_ACCESS_LEVEL':
        return {
            ...state,
            candidateSubmissionAccessLevel: action.payload,
        };
    case ContextTypeKeys.SET_QUESTION_STATE:
        return {
            ...state,
            questionState: {
                ...state.questionState,
                ...action.payload,
            },
        };
    case CommonContextActionTypes.REPLACE_CONTEXT:
        return {
            ...state,
            ...action.payload,
        };
    case ContextTypeKeys.SET_GRADING_WORKFLOW_INFO:
        return {
            ...state,
            workflowInfo: action.payload,
        };
    case ContextTypeKeys.UPDATE_WORKFLOW_INFO:
        return {
            ...state,
            workflowInfo: {
                ...state.workflowInfo,
                ...action.payload,
            },
        };
    case ContextTypeKeys.TOGGLE_DETAILS_PANEL:
        return {
            ...state,
            showDetailsPanel: !state.showDetailsPanel,
        };
    case ContextTypeKeys.OPEN_RECORDING_PANEL:
        return {
            ...state,
            recordingContextValues: action.payload,
        };
    case ContextTypeKeys.OPEN_RECORDING_PANEL_DONE:
        return {
            ...state,
            recordingContextValues: null,
        };
    case ContextTypeKeys.ASSESSMENT_RUN_SESSION_LOADED:
        return {
            ...state,
            assessmentRunSession: action.payload,
        };
    case ContextTypeKeys.TOGGLE_SPEAKING_TEST_ID_DATA_OPEN_SCREEN:
        return {
            ...state,
            openScreenIdVerification: true,
        };
    case ContextTypeKeys.SPEAKING_QUESTION_SET_IDS_LOADED:
        return {
            ...state,
            speakingQuestionSetIds: action.payload,
        };
    case ContextTypeKeys.SET_SPEAKING_SELECTED_CONTENT_ITEM_ID:
        return {
            ...state,
            speakingSelectedContentItemId: action.payload,
        };
    case ContextTypeKeys.TOGGLE_WORKSPACE_DETAILED_VIEW:
        return {
            ...state,
            showDetailedView: !state.showDetailedView,
        };
    }
    return state;
};
